import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react'
import PropTypes from 'prop-types'

import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'

import makeStyles from '@material-ui/core/styles/makeStyles'

import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isEqual, isEmpty } from 'lodash'
import ReactTooltip from 'react-tooltip'

import { usePrice } from '../../../../../shared/contexts/PriceContext'

import { PRODUCT_JUDICIAL_GUARANTEE_APPEAL } from '../../../../../constants'

import {
  ButtonLight,
  ButtonGradient,
} from '../../../../../shared/components'
import { GlobalMessages } from '../../../../../shared/messages'
import CollapsePrice from '../../../../../shared/components/CollapsePrice/CollapsePrice'
import DropdownSelectOne from '../../../../../shared/components/DropdownSelectOne/DropdownSelectOne'

import useProductQuotationClient from '../../../../../clients/ProductClient/useProductQuotationClient'
import DetailsProductData from '../../../../../models/JudicialGuaranteeAppealModels/DetailsProductData'
import ConfidenceTooltip from '../../ConfidenceTooltip'

const useStyles = makeStyles((theme) => ({
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
      [theme.breakpoints.down('xs')]: {
        marginTop: 5,
        marginLeft: 5,
        minWidth: 115,
      },
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  subLabelNoBadge: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
    marginTop: '4px',
  },
}))

const DetailsProduct = forwardRef((props, ref) => {
  const classes = useStyles()
  const {
    quote,
    quoteAiData,
    checked,
    handleStep,
    handleNext,
    handlePrevious,
  } = props

  const {
    onNext,
    actionNext,
    setFormikChanged,
    setResourceTypeProduct,
    judicialGuaranteeAppeal,
    setJudicialGuaranteeAppeal,
    setAppealSelected,
  } = usePrice()

  const productQuotationClient = useProductQuotationClient()

  const [modalities, setModalities] = useState([])
  const [appealTypes, setAppealTypes] = useState([])

  const showConfidenceToolTip = quoteAiData && quoteAiData.status === 'Processed' && quoteAiData.confidences

  const getAppealObject = useCallback((appealSelected) => (
    appealTypes.find((appealType) => appealSelected === appealType?.value)
  ), [appealTypes])

  const initialValuesForm = useMemo(() => (Object.assign(
    DetailsProductData(quote?.DetailsProduct ?? {}),
  )), [quote?.DetailsProduct])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesForm,
    validationSchema: Yup.object({
      appealType: Yup.string().required(),
      modality: Yup.string().required(),
    }),
    onSubmit: (data) => {
      const values = {
        ...judicialGuaranteeAppeal,
        ...{ detailsProduct: { ...data } },
      }
      setJudicialGuaranteeAppeal(values)

      if (actionNext) {
        setFormikChanged(!isEqual(initialValuesForm, data))
        onNext(values)
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onSubmit: () => new Promise((resolve) => {
      formik.validateForm().then((errors) => {
        let data

        if (!isEmpty(errors)) {
          data = { message: GlobalMessages.VERIFIQUE_CAMPOS_DESTAQUE_ANTES_PROSSEGUIR }
        }

        resolve(data)
        formik.submitForm()
      })
    }),
  }))

  useEffect(() => {
    const productJudicialGuaranteeAppeal = productQuotationClient().getProducts()
      .find((p) => p?.value === PRODUCT_JUDICIAL_GUARANTEE_APPEAL)
    setAppealTypes(productJudicialGuaranteeAppeal?.appeals?.sort() || [])
    setModalities(productJudicialGuaranteeAppeal?.subModalities?.sort() || [])
  }, [productQuotationClient])

  const mapAiValueToOption = useCallback((aiValue, isAppeal = false) => {
    if (!aiValue) return ''

    if (isAppeal) {
      const appeal = appealTypes.find((opt) => opt.text?.toLowerCase().trim()
       === aiValue?.toLowerCase().trim())
      return appeal?.value || ''
    }

    const modalityMap = {
      'depósito recursal - substituição': 'Judicial Depósito Recursal Substituição',
      'depósito recursal': 'Judicial Depósito Recursal',
    }

    const normalizedAi = aiValue.toLowerCase().trim()
    const mappedValue = modalityMap[normalizedAi]

    if (mappedValue) {
      return mappedValue
    }

    return modalities.find((mod) => mod.toLowerCase().includes(normalizedAi.replace(/\s-\s/g, ' '))) || aiValue
  }, [appealTypes, modalities])

  useEffect(() => {
    if (quote?.DetailsProduct && modalities.length > 0 && appealTypes.length > 0) {
      const modalityFromAI = quote.DetailsProduct.Modality
      const appealTypeFromAI = quote.DetailsProduct.AppealType

      const modalityValue = mapAiValueToOption(modalityFromAI, false)
      const appealValue = mapAiValueToOption(appealTypeFromAI, true)

      if (modalityValue && modalityValue !== formik.values.modality) {
        formik.setFieldValue('modality', modalityValue)
      }

      if (appealValue && appealValue !== formik.values.appealType) {
        formik.setFieldValue('appealType', appealValue)
        const appealObject = getAppealObject(appealValue)
        if (appealObject) {
          setAppealSelected(appealObject?.text)
          setResourceTypeProduct(appealObject?.id)
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.DetailsProduct, modalities, appealTypes, mapAiValueToOption, getAppealObject])

  const modalityLabel = 'Modalidade'
  const appealLabel = 'Tipo de Recurso'

  return (
    <>
      <CollapsePrice
        checked={checked}
        handleEdit={handleStep}
        hiddenEdit={isEmpty(quote?.DetailsProduct) && !formik.isSubmitting}
        title="Detalhes do Produto"
        subtitle="Informe abaixo os detalhes do produto"
      >
        <Grid container spacing={5}>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="modalityLabel" className={quoteAiData.sources?.laborActionStepRecursal?.imageText ? classes.label : classes.subLabelNoBadge}>
                {modalityLabel}
                {showConfidenceToolTip && quoteAiData.sources?.laborActionStepRecursal?.imageText
                && (
                <ConfidenceTooltip
                  text={quoteAiData.sources?.laborActionStepRecursal?.imageText}
                  imageUrl={quoteAiData.sources?.laborActionStepRecursal?.imageUrl}
                  confidence={quoteAiData?.confidences?.laborActionStepRecursal}
                />
                )}
              </label>
              <DropdownSelectOne
                id="modality"
                options={modalities}
                placeholder="Selecione"
                {...formik.getFieldProps('modality')}
                error={formik.touched.modality && !!formik.errors.modality}
                helpertext={formik.touched.modality && formik.errors.modality}
              />
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="appealLabel" className={quoteAiData.sources?.resourceTypeRecursal?.imageText ? classes.label : classes.subLabelNoBadge}>
                {appealLabel}
                {showConfidenceToolTip && quoteAiData.sources?.resourceTypeRecursal?.imageText
                && (
                <ConfidenceTooltip
                  text={quoteAiData.sources?.resourceTypeRecursal?.imageText}
                  imageUrl={quoteAiData.sources?.resourceTypeRecursal?.imageUrl}
                  confidence={quoteAiData?.confidences?.resourceTypeRecursal}
                />
                )}
              </label>
              <DropdownSelectOne
                id="appealType"
                options={appealTypes}
                placeholder="Selecione"
                {...formik.getFieldProps('appealType')}
                error={formik.touched.appealType && !!formik.errors.appealType}
                helpertext={formik.touched.appealType && formik.errors.appealType}
                onChange={(e) => {
                  const { value } = e.target
                  const appealObject = getAppealObject(value)
                  setAppealSelected(appealObject?.text)
                  formik.setFieldValue('appealType', appealObject?.value)
                  setResourceTypeProduct(appealObject?.id)
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.buttonsStep}>
              <Box>
                <ButtonLight
                  onClick={handlePrevious}
                  title="Voltar"
                >
                  Voltar
                </ButtonLight>
              </Box>
              <Box ml={4}>
                <ButtonGradient
                  onClick={handleNext}
                  title="Continuar"
                >
                  Continuar
                </ButtonGradient>
              </Box>
            </Box>
            <ReactTooltip place="top" type="dark" />
          </Grid>
        </Grid>
      </CollapsePrice>
    </>
  )
})

DetailsProduct.propTypes = {
  quote: PropTypes.any,
  quoteAiData: PropTypes.any,
  checked: PropTypes.bool,
  children: PropTypes.any,
  handleStep: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
}

DetailsProduct.defaultProps = {
  children: '',
  checked: false,
  quote: undefined,
  quoteAiData: undefined,
}

export default DetailsProduct
